// ext-origin is the param used to identify meta landings
// we remove it as soon as we land on flight details page on mdot and Search results on desktop(Flight details is a modal on SR in Desktop)
// so subsequent page views, page reloads are not identified as meta landings
//ref:server/modules/report-meta-landing.ts
import { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { flightsSessionStore } from "@flights/web-api-utils-universal";
import { META_LANDING_URL_KEY } from "../constants";
import { useTrackCurrencySelectionWithPosSwitch } from "../utils/experiments/pricing/flights_currency_selection_with_pos_switch";
import useRouteName from "./useRouteName";

const useRemoveExternalParamsFromUrl = () => {
  const history = useHistory();
  const { trackCurrencySelectionWithPosSwitch } = useTrackCurrencySelectionWithPosSwitch(); // flights_currency_selection_with_pos_switch
  const routeName = useRouteName();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    let needsUpdate = false;

    // used from reportCaPriceEntrypointAccuracy
    if (routeName !== "flightdetails") {
      const caPrice = urlParams.get("ca_price") || "";
      if (caPrice) {
        urlParams.delete("ca_price");
        needsUpdate = true;
      }
    }

    /**
     * flights_currency_selection_with_pos_switch
     * Handle currency selection after META landing
     *
     * When a user explicitly selects a currency after arriving from a META landing page,
     * we need to reset salesCountry and salesCurrency from URL params and session storage to prevent conflicts.
     *
     * We check for:
     * - salesCountry in session storage (indicates META landing occurred)
     * - selected_currency in URL params (indicates explicit currency selection)
     *
     * This ensures the user's currency choice takes precedence over META's POS settings.
     */
    const shouldClearMetaCurrencyParams =
      trackCurrencySelectionWithPosSwitch() &&
      flightsSessionStore.get("salesCountry") &&
      urlParams.get("selected_currency");
    if (shouldClearMetaCurrencyParams) {
      urlParams.delete("salesCountry");
      urlParams.delete("salesCurrency");

      flightsSessionStore.unset("salesCountry");
      flightsSessionStore.unset("salesCurrency");

      needsUpdate = true;
    }

    const metaChannel = urlParams.get("ext-origin") || "";
    if (metaChannel) {
      // Save meta landing url for grumbleDiscrepancy @see frontend/src/hooks/useReportPriceDiscrepancy.ts
      flightsSessionStore.set(META_LANDING_URL_KEY, window.location.href);

      urlParams.delete("ext-origin");
      urlParams.delete("ext-fwd");
      urlParams.delete("salesCountry");

      const metaChannelCustomerLocale = urlParams.get("customerLanguage") || "";
      // META-1053: for meta landings, we currently receive locale and customerLanguage param from ETG. We are going to start accepting customer's meta site selected language and replacing our locale with it.
      // refer request-locale.ts:19
      if (metaChannelCustomerLocale) {
        urlParams.set("locale", metaChannelCustomerLocale.toLowerCase());
        urlParams.delete("customerLanguage");
      }
      needsUpdate = true;
    }

    if (needsUpdate) {
      history.replace(`${window.location.pathname}?${urlParams.toString()}${window.location.hash}`);
    }
  }, [history, routeName, trackCurrencySelectionWithPosSwitch]);
};

export default useRemoveExternalParamsFromUrl;
